import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(
        language === 'nl'
          ? 'Er ging iets mis. Probeer het later opnieuw.'
          : 'Something went wrong. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 shadow-lg text-center">
            <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-primary dark:text-white mb-4">
              {language === 'nl' ? 'E-mail verzonden' : 'Email sent'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {language === 'nl'
                ? 'Als dit e-mailadres bij ons bekend is, ontvang je binnen enkele minuten een e-mail met instructies om je wachtwoord te herstellen.'
                : 'If this email address is registered with us, you will receive an email with instructions to reset your password within a few minutes.'}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-accent hover:text-accent-hover font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'nl' ? 'Terug naar inloggen' : 'Back to login'}
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-8">
          <h1 className="font-heading font-bold text-2xl text-primary dark:text-white mb-2">
            {language === 'nl' ? 'Wachtwoord vergeten?' : 'Forgot password?'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            {language === 'nl'
              ? 'Vul je e-mailadres in en we sturen je een link om je wachtwoord te herstellen.'
              : 'Enter your email address and we will send you a link to reset your password.'}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                E-mail
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder={language === 'nl' ? 'naam@bedrijf.nl' : 'name@company.com'}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-accent focus:border-transparent"
                  data-testid="forgot-password-email"
                />
              </div>
            </div>

            {error && (
              <p className="text-error text-sm">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-6 rounded-xl bg-accent text-white font-medium hover:bg-accent-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="forgot-password-submit"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                language === 'nl' ? 'Verstuur reset link' : 'Send reset link'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-accent text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'nl' ? 'Terug naar inloggen' : 'Back to login'}
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
